import { useLanguage } from '@/contexts/LanguageContext';

export default function AboutWhoWeAre() {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const isJa = lang === 'ja';

  const infoCards = isEn
    ? [
        { icon: 'ri-building-2-line', iconColor: 'text-brand-green', iconBg: 'bg-brand-green-light', title: 'Core Business', items: ['Chemical Material Manufacturing', 'Plastic Additive R&D', 'Processed Plastic Pellet Manufacturing'] },
        { icon: 'ri-flask-line', iconColor: 'text-brand-blue', iconBg: 'bg-brand-blue-light', title: 'Core Products', items: ['PQernel®', 'PQMB', 'rfPlastics', 'rvPET', 'rvPlastics'] },
        { icon: 'ri-team-line', iconColor: 'text-brand-green', iconBg: 'bg-brand-green-light', title: 'Target Clients', items: ['Plastic Recyclers', 'Compounders', 'Injection/Extrusion Manufacturers', 'Brand ESG Projects'] },
      ]
    : isJa
    ? [
        { icon: 'ri-building-2-line', iconColor: 'text-brand-green', iconBg: 'bg-brand-green-light', title: '主要事業内容', items: ['化学材料製造', 'プラスチック添加剤研究開発', '加工プラスチックペレット製造'] },
        { icon: 'ri-flask-line', iconColor: 'text-brand-blue', iconBg: 'bg-brand-blue-light', title: 'コア製品', items: ['PQernel®', 'PQMB', 'rfPlastics', 'rvPET', 'rvPlastics'] },
        { icon: 'ri-team-line', iconColor: 'text-brand-green', iconBg: 'bg-brand-green-light', title: 'サービス対象', items: ['プラスチックリサイクル業者', '造粒業者', '射出・押出メーカー', 'ブランドESGプロジェクト'] },
      ]
    : [
        { icon: 'ri-building-2-line', iconColor: 'text-brand-green', iconBg: 'bg-brand-green-light', title: '主要營業項目', items: ['化工材料製造', '塑膠添加劑研發', '加工塑膠粒製造'] },
        { icon: 'ri-flask-line', iconColor: 'text-brand-blue', iconBg: 'bg-brand-blue-light', title: '核心產品', items: ['PQernel®', 'PQMB', 'rfPlastics', 'rvPET', 'rvPlastics'] },
        { icon: 'ri-team-line', iconColor: 'text-brand-green', iconBg: 'bg-brand-green-light', title: '服務對象', items: ['塑膠回收廠', '造粒廠', '射出押出製造廠', '品牌端 ESG 專案'] },
      ];

  const sectionLabel = isEn ? 'Who We Are' : isJa ? '私たちについて' : '我們是誰';
  const heading = isEn ? 'Who We Are' : isJa ? '私たちについて' : '我們是誰？';

  const body = isEn ? (
    <>PolyQ Applied Materials is a Taichung-based functional materials and circular economy company. Our core technology is the{' '}
      <strong className="text-neutral-dark font-semibold">PQernel® nano-scale polymer chain extender</strong>
      {' '}and its extended applications. We serve plastic recyclers, compounders, injection molders, and brand owners — transforming waste and off-grade plastics that would otherwise be incinerated or downcycled into high-value recycled materials ready for stable production.
    </>
  ) : isJa ? (
    <>聚量応用材料は台湾・台中を拠点とする機能性材料・サーキュラーエコノミー企業です。コア技術は
      <strong className="text-neutral-dark font-semibold"> PQernel®ナノスケール高分子鎖延長補強剤</strong>
      とその応用技術です。プラスチックリサイクル業者・造粒業者・射出成形メーカー・ブランドオーナーにサービスを提供し、焼却やダウンサイクルされていた廃棄・低品位プラスチックを、安定した生産ラインに再投入できる高付加価値の再生材料へと転換します。
    </>
  ) : (
    <>聚量應用材料是一家立基於台中的功能型材料與循環經濟公司，核心技術為
      <strong className="text-neutral-dark font-semibold"> PQernel® 奈米級高分子擴鏈補強劑</strong>
      及其延伸應用，服務塑膠回收廠、造粒廠、塑膠射出與終端品牌，將原本需焚化或降階使用的廢棄與次級塑料，升級為可穩定再投入產線的高價值再生材料。
    </>
  );

  return (
    <section className="section-padding bg-white">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">{sectionLabel}</p>
            <h2 className="font-bold text-neutral-dark mb-5" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}>{heading}</h2>
            <p className="text-neutral-mid" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>{body}</p>
          </div>
          <div className="grid grid-cols-1 gap-4">
            {infoCards.map((card) => (
              <div key={card.title} className="flex items-start gap-4 p-5 rounded-card bg-[#F5F8FB] border border-neutral-border">
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${card.iconBg} flex-shrink-0`}>
                  <i className={`${card.icon} ${card.iconColor} text-xl`}></i>
                </div>
                <div>
                  <p className="font-semibold text-neutral-dark text-[1.0625rem] mb-2">{card.title}</p>
                  <div className="flex flex-wrap gap-2">
                    {card.items.map((item) => (
                      <span key={item} className="inline-block px-2.5 py-0.5 rounded-full bg-white border border-neutral-border text-neutral-mid text-[0.9375rem]">{item}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
